import type { GeometryKey } from '../locales/keys';

export interface AnglePreset {
  id: string;
  mode: 'around-a-point' | 'parallel-lines' | 'polygon-angles' | 'double-transversal';
  labelKey: GeometryKey;
  advancedOnly: boolean;
  // around-a-point specific
  rayAngles?: number[];
  anglesOnLine?: boolean;
  // parallel-lines specific
  transversalAngle?: number;
}

export const ANGLE_PRESETS: readonly AnglePreset[] = [
  // Foundation: around-a-point
  {
    id: 'straight-line',
    mode: 'around-a-point',
    labelKey: 'angle.preset.straight-line',
    advancedOnly: false,
    rayAngles: [0, 180],
    anglesOnLine: true,
  },
  {
    id: 'full-turn',
    mode: 'around-a-point',
    labelKey: 'angle.preset.full-turn',
    advancedOnly: false,
    rayAngles: [0, 90, 180, 270],
    anglesOnLine: false,
  },

  // Foundation: parallel-lines
  {
    id: 'z-angles',
    mode: 'parallel-lines',
    labelKey: 'angle.preset.z-angles',
    advancedOnly: false,
    transversalAngle: 30,
  },
  {
    id: 'c-angles',
    mode: 'parallel-lines',
    labelKey: 'angle.preset.c-angles',
    advancedOnly: false,
    transversalAngle: 60,
  },
  {
    id: 'f-angles',
    mode: 'parallel-lines',
    labelKey: 'angle.preset.f-angles',
    advancedOnly: false,
    transversalAngle: 45,
  },

  // Advanced: polygon angles
  {
    id: 'pentagon-fan',
    mode: 'polygon-angles',
    labelKey: 'angle.preset.pentagon-fan',
    advancedOnly: true,
  },
  {
    id: 'hexagon-turtle',
    mode: 'polygon-angles',
    labelKey: 'angle.preset.hexagon-turtle',
    advancedOnly: true,
  },

  // Advanced: double transversal
  {
    id: 'double-transversal',
    mode: 'double-transversal',
    labelKey: 'angle.preset.double-transversal',
    advancedOnly: true,
  },
];

/**
 * Validate that a preset has all required fields and valid structure.
 */
export function isValidPreset(p: AnglePreset): boolean {
  // id must be non-empty string
  if (typeof p.id !== 'string' || p.id.length === 0) {
    return false;
  }

  // mode must be one of the allowed values
  const validModes = ['around-a-point', 'parallel-lines', 'polygon-angles', 'double-transversal'];
  if (!validModes.includes(p.mode)) {
    return false;
  }

  // labelKey must be string
  if (typeof p.labelKey !== 'string' || p.labelKey.length === 0) {
    return false;
  }

  // advancedOnly must be boolean
  if (typeof p.advancedOnly !== 'boolean') {
    return false;
  }

  // Mode-specific validations
  if (p.mode === 'around-a-point') {
    if (p.rayAngles !== undefined) {
      if (!Array.isArray(p.rayAngles) || !p.rayAngles.every((x) => typeof x === 'number')) {
        return false;
      }
    }
    if (p.anglesOnLine !== undefined && typeof p.anglesOnLine !== 'boolean') {
      return false;
    }
  } else if (p.mode === 'parallel-lines') {
    if (p.transversalAngle !== undefined && typeof p.transversalAngle !== 'number') {
      return false;
    }
  }

  return true;
}

/**
 * Validate that all presets have unique IDs.
 */
export function validatePresetIds(presets: readonly AnglePreset[]): boolean {
  const ids = new Set<string>();
  for (const preset of presets) {
    if (ids.has(preset.id)) {
      return false;
    }
    ids.add(preset.id);
  }
  return true;
}
